import { Combobox } from '@headlessui/react'
import { highlightSearch, usePopperSelectDropdown } from '../../../ui/utils'
import { useContext, useMemo } from 'react'
import { VariablesContext } from './RuleHelpers'
import { ButtonGroup } from '../../../ui'
import { ChevronUpDownIcon } from '../../../ui/icons'
import clsx from 'clsx'

// Limit options to prevent Headless UI focus-management performance issues
// (sortByDomNode uses O(n²) compareDocumentPosition calls)
const MAX_OPTIONS = 50

export default function RuleEventName({ value, onChange, required, disabled, size = 'small' }: {
    value: string
    onChange: (value: string) => void
    required?: boolean
    disabled?: boolean
    size?: 'small' | 'regular'
}) {
    const {
        setReferenceElement,
        setPopperElement,
        attributes,
        styles,
    } = usePopperSelectDropdown()

    const { suggestions } = useContext(VariablesContext)

    const filteredEventNames = useMemo(() => {
        return Object.keys(suggestions.eventPaths)
            .sort()
            .filter(eventName => !value || eventName.toLowerCase().startsWith(value.toLowerCase()))
            .slice(0, MAX_OPTIONS)
    }, [suggestions.eventPaths, value])

    return (
        <Combobox onChange={(v: string | null) => v && onChange(v)} as="div" className="ui-select">
            <ButtonGroup>
                <span className="ui-text-input" style={{ flex: 1 }}>
                    <Combobox.Input
                        value={value}
                        onChange={e => onChange(e.target.value)}
                        required={required}
                        disabled={disabled}
                        className={size}
                        ref={setReferenceElement}
                    />
                </span>
                <Combobox.Button className={clsx('ui-button secondary', size)} disabled={disabled}>
                    <ChevronUpDownIcon />
                </Combobox.Button>
            </ButtonGroup>
            <Combobox.Options
                className="select-options nowheel"
                ref={setPopperElement}
                style={styles.popper}
                {...attributes.popper}
            >
                {
                    filteredEventNames.length
                        ? filteredEventNames.map(eventName => (
                            <Combobox.Option
                                key={eventName}
                                value={eventName}
                                className={({ focus, selected }) => clsx('select-option', focus && 'active', selected && 'selected')}
                            >
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: highlightSearch(eventName, value),
                                    }}
                                />
                            </Combobox.Option>
                        ))
                        : <li className="select-option-empty">No Results</li>
                }
            </Combobox.Options>
        </Combobox>
    )
}
